import axios, {AxiosResponse} from "axios";
import {api, getApiError, handleApiError, ServiceResponse} from "../ApiClient";

export interface UpdateUser {
    firstName: string
    lastName: string
    phoneNumber: string
    bio: string
    nationality: string
    languages: string[]
    headline: string
    instagramHandle: string
    twitterHandle: string
    background: string
    clubExperience: number
    tourExperience: number
}

export interface User {
    id: string
    firstName: string
    lastName: string
    displayName: string
    email: string
    phoneNumber: string
    bio: string
    nationality: string
    languages: string[]
    headline: string
    instagramHandle: string
    twitterHandle: string
    background: string
    clubExperience: number
    tourExperience: number
}

export interface GetUserResponse extends ServiceResponse {
    user?: User
}

export interface AvatarResponse extends ServiceResponse {
    avatar?: string
}

export interface CreateUserResponse extends ServiceResponse {
    user?: User
}

export const UserService = () => {

    const getCurrentUser = (): Promise<GetUserResponse> => {
        return api.get<User>(`/api/1/user`)
            .then((resp) => {
                if (resp && resp.status === 200 && resp.data) {
                    return {
                        user: resp.data
                    }
                }

                // this should probably be replicated throughout the code base...
                // does this mean no api error messages are getting sent to the app?
                let e = getApiError(resp)
                return {
                    error: {
                        apiError: e,
                        message: e.message ? e.message : "Unknown error occurred."
                    }
                }
            })
            .catch(handleApiError)
    };

    const getAvatar = async (userId: string): Promise<AvatarResponse> => {
        return api.get<string>(`/api/1/user/${userId}/avatar/new`)
            .then((resp) => {
                if (resp && resp.status === 200 && resp.data) {
                    return {
                        avatar: resp.data
                    }
                }

                return {
                    error: {
                        message: 'Could not get avatar.'
                    }
                }
            })
            .catch(handleApiError)
    };

    const createUser = async (firstName: string, lastName: string): Promise<CreateUserResponse> => {
        return api.post<User>(`/api/1/user`, {
            firstName: firstName,
            lastName: lastName
        })
            .then((resp) => {
                if (resp && resp.status === 200 && resp.data) {
                    return {
                        user: resp.data
                    }
                }

                return {
                    error: {
                        message: 'Could not create user profile.'
                    }
                }
            })
            .catch(handleApiError)
    };

    const updateUser = async (id: string, user: UpdateUser): Promise<CreateUserResponse> => {
        return api.put<User>(`/api/1/user/${id}`, {
            ...user,
            id
        })
            .then((resp) => {
                if (resp && resp.status === 200 && resp.data) {
                    return {
                        user: resp.data
                    }
                }

                let e = getApiError(resp)
                return {
                    error: {
                        apiError: e,
                        message: e.message ? e.message : "Unknown error occurred."
                    }
                }
            })
            .catch(handleApiError)
    };

    const uploadAvatar = async (uuid: string, imageUri: any): Promise<ServiceResponse> => {
        return api.put<string>(`/api/1/user/${uuid}/avatar/new`)
            .then(async (resp: AxiosResponse<string>) => {
                try {
                    let resp1 = await axios.put(resp.data, imageUri, {
                        headers: {'Content-Type': 'multipart/form-data'},
                    });
                    if (resp1.status === 200) {
                        return {}
                    }

                    let e = getApiError(resp1)
                    return {
                        error: {
                            apiError: e,
                            message: e.message ? e.message : "Unknown error occurred."
                        }
                    }
                } catch (e1) {
                    return {
                        error: {
                            message: 'Unknown error occurred uploading avatar.'
                        }
                    }
                }
            })
            .catch(handleApiError)
    };


    const deleteUser = async (userId: string): Promise<ServiceResponse> => {
        return api.delete(`/api/1/user/${userId}`)
            .then((resp) => {
                if (resp && resp.status === 202) {
                    return {}
                }

                return {
                    error: {
                        message: 'Could not get avatar.'
                    }
                }
            })
            .catch(handleApiError)
    };

    return {
        getCurrentUser,
        getAvatar,
        createUser,
        updateUser,
        uploadAvatar,
        deleteUser
    }
}
